import React, { useMemo, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Box, Typography, Paper, Avatar, Chip, CircularProgress, Autocomplete, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
// Import icons from RiderLiveMap assets
import CarIcon from '../../../RiderLiveMap/assets/car.png';
import HeavyTruckIcon from '../../../RiderLiveMap/assets/heavy_truck-01.png';
import LightTruckIcon from '../../../RiderLiveMap/assets/light_truck.png';
import BicycleIcon from '../../../RiderLiveMap/assets/bicycle-01.png';
import MotorCyleIcon from '../../../RiderLiveMap/assets/motorcyle.png';
import VanIcon from '../../../RiderLiveMap/assets/van.png';
import RiderGreen from '../../../RiderLiveMap/assets/rider_green.png';
import RiderRed from '../../../RiderLiveMap/assets/rider_red.png';
import RiderBlue from '../../../RiderLiveMap/assets/rider_blue.png';
import RiderBlack from '../../../RiderLiveMap/assets/rider_black.png';
import RiderYellow from '../../../RiderLiveMap/assets/rider_yellow.png';
import mapStyle from '../../../RiderLiveMap/mapStyle';
import {
    BICYCLE, MOTORCYCLE, LIGHT_TRUCK, HEAVY_TRUCK, CAR, VAN, API_KEY,
    RIDER_ASSIGNED, RIDER_PICKED_UP, RIDER_BUSY, RIDER_RETURNED,
    RIDER_UNDELIVERED, RIDER_DEPARTED, RIDER_FREED, RIDER_RETURN_FREED, RIDER_ON_BREAK
} from 'src/utils/pnd/PndConstants';

const DashboardLiveMap = ({ riders }) => {
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: API_KEY
    });

    const [selectedRider, setSelectedRider] = useState(null);
    const [map, setMap] = useState(null);

    const onMapLoad = React.useCallback((mapInstance) => {
        setMap(mapInstance);
    }, []);


    const isValidCoord = (lat, lng) => {
        const latitude = parseFloat(lat);
        const longitude = parseFloat(lng);
        return !isNaN(latitude) && !isNaN(longitude) && latitude !== 0 && longitude !== 0;
    };

    const center = useMemo(() => {
        if (riders && riders.length > 0) {
            const validRiders = riders.filter(r => isValidCoord(r.latitude, r.longitude));
            if (validRiders.length > 0) {
                return {
                    lat: parseFloat(validRiders[0].latitude),
                    lng: parseFloat(validRiders[0].longitude)
                };
            }
        }
        return { lat: 25.276987, lng: 55.296249 }; // Dubai
    }, [riders]);

    const handleSearchChange = (event, newValue) => {
        if (newValue && map && isValidCoord(newValue.latitude, newValue.longitude)) {
            const position = {
                lat: parseFloat(newValue.latitude),
                lng: parseFloat(newValue.longitude)
            };
            map.panTo(position);
            map.setZoom(17);
            setSelectedRider(newValue);
        }
    };

    const containerStyle = {
        width: '100%',
        height: '450px',
        borderRadius: '12px',
    };

    const getMarkerIcon = (statusId) => {
        let icon;
        switch (Number(statusId)) {
            case RIDER_FREED:
            case RIDER_RETURN_FREED:
            case RIDER_RETURNED:
                icon = RiderGreen;
                break;
            case RIDER_PICKED_UP:
            case RIDER_DEPARTED:
            case RIDER_BUSY:
            case RIDER_UNDELIVERED:
                icon = RiderRed;
                break;
            case RIDER_ASSIGNED:
                icon = RiderBlue;
                break;
            case RIDER_ON_BREAK:
                icon = RiderYellow;
                break;
            default:
                icon = RiderBlack;
                break;
        }

        return {
            url: icon,
            scaledSize: new window.google.maps.Size(42, 42),
            anchor: new window.google.maps.Point(21, 21),
            labelOrigin: new window.google.maps.Point(21, -10)
        };
    };

    if (!isLoaded) return (
        <Box sx={{ height: 450, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', mb: 3 }}>
            <CircularProgress sx={{ color: '#32CA73', mb: 2 }} />
            <Typography variant="body2" color="textSecondary">Loading Live Markers...</Typography>
        </Box>
    );

    return (
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', mb: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 1, width: { xs: '80%', sm: '300px' } }}>
                <Autocomplete
                    size="small"
                    options={(riders || []).filter(r => isValidCoord(r.latitude, r.longitude))}
                    getOptionLabel={(option) => `${option.riderName} (${option.plateNo})`}
                    onChange={handleSearchChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Find Rider..."
                            variant="outlined"
                            sx={{
                                bgcolor: 'white',
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#e2e8f0' },
                                    '&:hover fieldset': { borderColor: '#32CA73' },
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                />
            </Box>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={11}
                onLoad={onMapLoad}
                options={{
                    styles: mapStyle,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: true,
                    zoomControlOptions: {
                        position: 3 // RIGHT_BOTTOM
                    }
                }}
            >
                {riders && riders.filter(r => isValidCoord(r.latitude, r.longitude)).map((rider, index) => (
                    <Marker
                        key={index}
                        position={{ lat: parseFloat(rider.latitude), lng: parseFloat(rider.longitude) }}
                        icon={getMarkerIcon(rider.riderStatusId)}
                        onClick={() => setSelectedRider(rider)}
                        title={rider.riderName}
                    />
                ))}

                {selectedRider && isValidCoord(selectedRider.latitude, selectedRider.longitude) && (
                    <InfoWindow
                        position={{ lat: parseFloat(selectedRider.latitude), lng: parseFloat(selectedRider.longitude) }}
                        onCloseClick={() => setSelectedRider(null)}
                    >
                        <Box sx={{ p: 1, minWidth: 150 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar sx={{ bgcolor: '#32CA73', mr: 1, width: 24, height: 24, fontSize: '0.7rem' }}>
                                    {selectedRider.riderName ? selectedRider.riderName[0].toUpperCase() : 'R'}
                                </Avatar>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {selectedRider.riderName}
                                </Typography>
                            </Box>
                            <Typography variant="caption" display="block" sx={{ color: '#64748b', mb: 0.5 }}>
                                {selectedRider.plateNo} • {selectedRider.riderStatus}
                            </Typography>
                            <Chip
                                label={selectedRider.storeName || 'No Hub'}
                                size="small"
                                sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#f1f5f9' }}
                            />
                        </Box>
                    </InfoWindow>
                )}
            </GoogleMap>
        </Paper>
    );
};

export default DashboardLiveMap;
